import { Dialog, Transition } from '@headlessui/react'
import { Fragment, useCallback, useState } from 'react'
import { createPortal } from 'react-dom'

export type ActionIntent = 'primary' | 'danger' | 'default'

export type Action<A extends string> = {
  id: A
  label: string
  intent?: ActionIntent
  autoFocus?: boolean
}

type ConfirmOptions<A extends string> = {
  title: string
  message: React.ReactNode
  actions: Action<A>[]
  checkboxLabel?: string
}

type ConfirmResult<A extends string> = {
  actionId: A
  remember: boolean
}

export function useConfirmDialog<A extends string = string>() {
  type Resolver = (value: ConfirmResult<A>) => void

  const [state, setState] = useState<{
    open: boolean
    title: string
    message: React.ReactNode
    actions: Action<A>[]
    checkboxLabel?: string
    resolver?: Resolver
  }>({
    open: false,
    title: '',
    message: '',
    actions: [],
  })

  const [remember, setRemember] = useState(false)

  const confirm = useCallback((options: ConfirmOptions<A>) => {
    return new Promise<ConfirmResult<A>>((resolve) => {
      setRemember(false)
      setState({ open: true, ...options, resolver: resolve })
    })
  }, [])

  const close = useCallback(() => {
    setState((previous) => ({ ...previous, open: false }))
  }, [])

  const choose = useCallback(
    (actionId: A) => {
      state.resolver?.({ actionId, remember })
      close()
    },
    [state, remember, close]
  )

  const DialogComponent = createPortal(
    <Transition.Root show={state.open} as={Fragment}>
      <Dialog as='div' className='relative z-modal' onClose={close}>
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
        </Transition.Child>
        <div className='fixed inset-0 z-modal overflow-y-auto'>
          <div className='flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
              enterTo='opacity-100 translate-y-0 sm:scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 translate-y-0 sm:scale-100'
              leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
            >
              <Dialog.Panel className='relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:min-w-[400px] sm:max-w-lg sm:p-6 dark:bg-gray-800'>
                <div className='text-center'>
                  <Dialog.Title
                    as='h3'
                    className='text-base font-semibold leading-6 text-gray-900 dark:text-gray-100'
                  >
                    {state.title}
                  </Dialog.Title>
                  <div className='mt-2 text-sm text-gray-500 dark:text-gray-400'>
                    {state.message}
                  </div>
                </div>

                {state.checkboxLabel && (
                  <div className='mt-5'>
                    <label className='flex items-center'>
                      <input
                        type='checkbox'
                        checked={remember}
                        onChange={(event) => {
                          setRemember(event.target.checked)
                        }}
                        className='h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:ring-offset-gray-800'
                      />
                      <span className='ml-2 text-sm text-gray-600 dark:text-gray-300'>
                        {state.checkboxLabel}
                      </span>
                    </label>
                  </div>
                )}

<div className='mt-5 sm:mt-6 flex flex-col gap-3 sm:flex-row'>
                  {state.actions.map((action) => {
                    const isPrimary = action.intent === 'primary'
                    const isDanger = action.intent === 'danger'

                    const classes = isPrimary
                      ? 'bg-blue-600 hover:bg-blue-500 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                      : isDanger
                        ? 'bg-red-600 hover:bg-red-500 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600'
                        : 'bg-white ring-1 ring-inset ring-gray-300 hover:bg-gray-50 text-gray-900 dark:bg-gray-700 dark:text-gray-100 dark:ring-gray-600 dark:hover:bg-gray-600'

                    return (
                      <button
                        key={action.id}
                        type='button'
                        autoFocus={Boolean(action.autoFocus)}
                        className={`inline-flex flex-1 justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm sm:min-w-[100px] ${classes}`}
                        onClick={() => {
                          choose(action.id)
                        }}
                      >
                        {action.label}
                      </button>
                    )
                  })}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>,
    document.body
  )

  return { confirm, DialogComponent }
}
